/**
 * ConversationList — the conversations panel (header + project-grouped session
 * list + row actions), shared verbatim by the desktop ChatHomeView rail and the
 * mobile MobileChatScreen drawer so the two surfaces can't drift. The data spine
 * is useChatHomeSessions; this owns only the render + the two bits of local UI
 * state (inline-rename target, 3-dots menu anchor).
 *
 * Surface differences are props, not forks:
 *   - `urlPrefix`  present → the per-group "open workspace in a new tab" button
 *                  (desktop, which has tabs); omit on mobile.
 *   - `touch`      → row/group controls are always visible instead of
 *                  hover-revealed (touch has no hover).
 *   - `onSelect`   → fired after a row/New opens a session, so the mobile drawer
 *                  can close itself; desktop leaves it undefined.
 */
import { useState, useCallback } from 'react'
import { MessageSquare, MessageCircle, Plus, LayoutGrid, Sparkles, MoreVertical, Pencil, Trash2, ExternalLink } from 'lucide-react'
import { projectDisplayName, type ProjectItem } from '@papan/files/ui'
import type { ChatSessionRow, ChatSessionGroup } from './useChatHomeSessions'

export function ConversationList({
  sessionGroups, busyIds, doneIds,
  activeProject, activeSessionId,
  openSession, renameSession, deleteSession,
  urlPrefix, touch = false, onSelect,
}: {
  sessionGroups: ChatSessionGroup[]
  busyIds: Set<string>
  doneIds: Set<string>
  activeProject: ProjectItem | null
  activeSessionId: string | null
  openSession: (project: ProjectItem, sessionId: string | null) => void
  renameSession: (project: ProjectItem, id: string, title: string) => Promise<void> | void
  deleteSession: (project: ProjectItem, session: ChatSessionRow) => void
  /** Desktop: base path for the per-group "open in new tab" link. Omit → hidden. */
  urlPrefix?: string
  /** Touch surface: show row/group controls always (no hover to reveal them). */
  touch?: boolean
  /** Fired after a session is opened/created — the mobile drawer closes on it. */
  onSelect?: () => void
}) {
  const [menuFor, setMenuFor] = useState<{ project: ProjectItem; session: ChatSessionRow; x: number; y: number } | null>(null)
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null)

  const commitRename = useCallback(async (project: ProjectItem, id: string, title: string) => {
    setEditing(null)
    await renameSession(project, id, title)
  }, [renameSession])

  const open = (project: ProjectItem, sid: string | null) => { openSession(project, sid); onSelect?.() }

  const rowMenuCls = touch ? 'opacity-100' : 'opacity-0 group-hover/srow:opacity-100'
  const headBtnCls = touch ? 'opacity-100' : 'opacity-0 group-hover/ghead:opacity-100'

  return (
    <>
      {/* Header — just the panel title; a chat starts from the hero composer,
          not from here (conversations no longer hang off a project). */}
      <div className="flex items-center gap-2 px-3.5 pt-3.5 pb-2 shrink-0">
        <MessageSquare size={14} className="text-purple-400" />
        <span className="text-xs font-semibold uppercase tracking-wide themed-text-secondary">Conversations</span>
      </div>

      {/* Grouped session list — assistant pinned first (sparkle), rest by recency. */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        {sessionGroups.map(({ project, sessions: rows, isAssistant }) => (
          <div key={project.id} className="mb-1">
            <div className="ws-home-heading group/ghead px-2 pt-2.5 pb-1">
              {isAssistant
                ? <Sparkles size={12} className="shrink-0 text-blue-400" aria-hidden />
                : <LayoutGrid size={12} className="chat-proj-icon shrink-0" aria-hidden />}
              <span className="truncate normal-case tracking-normal">{projectDisplayName(project.name)}</span>
              {!isAssistant && urlPrefix && (
                <button
                  type="button"
                  title="Open workspace in a new tab"
                  onClick={() => window.open(`${urlPrefix}/workspace/${project.folderId}`, '_blank', 'noopener')}
                  className={`shrink-0 rounded p-0.5 themed-text-muted hover:themed-text transition-opacity ${headBtnCls}`}
                >
                  <ExternalLink size={12} />
                </button>
              )}
              <span className="ml-auto flex items-center gap-0.5 shrink-0">
                <button
                  type="button"
                  title="New conversation"
                  onClick={() => open(project, null)}
                  className="rounded p-0.5 themed-text-muted hover:themed-text"
                >
                  <Plus size={13} />
                </button>
              </span>
            </div>
            {rows.map(s => {
              const running = busyIds.has(s.id)
              const done = doneIds.has(s.id)
              const isActive = activeProject?.id === project.id && activeSessionId === s.id
              const isEditing = editing?.id === s.id
              return (
                <div key={s.id} className="relative group/srow">
                  <button
                    onClick={() => { if (!isEditing) open(project, s.id) }}
                    className="ws-snav-item text-left"
                    data-active={isActive ? '' : undefined}
                    data-testid="chat-home-session"
                  >
                    <span className="chat-snav-lead">
                      {running ? (
                        <span className="chat-run-wave" title="Agent running" aria-hidden>
                          <span /><span /><span />
                        </span>
                      ) : done ? (
                        <span className="chat-done-dot" title="Finished while you were away" />
                      ) : (
                        <MessageCircle size={15} className="themed-text-muted" aria-hidden />
                      )}
                    </span>
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editing.title}
                        onChange={e => setEditing({ id: s.id, title: e.target.value })}
                        onBlur={() => void commitRename(project, s.id, editing.title)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') void commitRename(project, s.id, editing.title)
                          if (e.key === 'Escape') setEditing(null)
                        }}
                        onClick={e => e.stopPropagation()}
                        className="flex-1 min-w-0 bg-transparent text-[13px] themed-text outline-none border-b border-purple-500/60"
                      />
                    ) : (
                      <span className="flex-1 min-w-0 truncate text-[13px] text-left pr-4">{s.title || 'Untitled chat'}</span>
                    )}
                  </button>
                  {!isEditing && (
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        const r = e.currentTarget.getBoundingClientRect()
                        setMenuFor(prev => prev?.session.id === s.id ? null : { project, session: s, x: r.right, y: r.bottom })
                      }}
                      className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md themed-text-muted hover:themed-text transition-opacity ${rowMenuCls}`}
                      title="Conversation options"
                      aria-haspopup="menu"
                    >
                      <MoreVertical size={13} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        ))}
        {sessionGroups.length === 0 && (
          <p className="px-3 py-4 text-xs themed-text-muted">No conversations yet — say hello.</p>
        )}
      </div>

      {/* 3-dots menu — fixed position escapes the panel's scroll clipping. */}
      {menuFor && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuFor(null)} />
          <div
            className="ws-menu fixed z-50 min-w-[160px]"
            style={{ left: Math.max(8, menuFor.x - 160), top: menuFor.y + 4 }}
            role="menu"
          >
            <button
              role="menuitem"
              className="ws-menu-item"
              onClick={() => {
                setEditing({ id: menuFor.session.id, title: menuFor.session.title || '' })
                setMenuFor(null)
              }}
            >
              <Pencil size={13} aria-hidden />
              Rename
            </button>
            <button
              role="menuitem"
              className="ws-menu-item"
              style={{ color: '#f87171' }}
              onClick={() => {
                const { project, session } = menuFor
                setMenuFor(null)
                void deleteSession(project, session)
              }}
            >
              <Trash2 size={13} aria-hidden />
              Delete
            </button>
          </div>
        </>
      )}
    </>
  )
}
